import type React from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/Button/Button";
import { ChevronLeftIcon } from "@/components/Icons/ChevronLeftIcon";
import { Glasses02Icon } from "@/components/Icons/Glasses02Icon";
import { MessageHeartSquareIcon } from "@/components/Icons/MessageHeartSquareIcon";
import MultiLineContent from "@/components/MultiLineContent/MultiLineContent";
import { Paper } from "@/components/Paper/Paper";
import { Redirect } from "@/components/Routing/Redirect";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { userMutations } from "@/queries/users";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function OnboardingCodeOfConductPage(): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const slug = useSlug();

  const markCodeOfConductAsAgreedMutation = userMutations.useMarkCodeOfConductAsAgreed();

  const handleAgree = async () => {
    await markCodeOfConductAsAgreedMutation.mutateAsync();
    void navigate(routes.onboardingFlow.userProfile({ slug }));
  };

  if (sessionUser.codeOfConductAgreedAt) {
    return <Redirect to={({ slug: currSlug }) => routes.onboardingFlow.userProfile({ slug: currSlug })} />;
  }

  return (
    <main className="z-10 flex min-h-full w-full justify-center overflow-auto">
      <Paper theme="no-header-minimal">
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:w-[320px] md:p-6">
          <Button
            type="link"
            styling="ghostPrimary"
            href={routes.onboardingFlow.slides({
              slug,
            })}
            icon={<ChevronLeftIcon />}
          >
            {t("common.action.back")}
          </Button>
          <div className="flex flex-col gap-2">
            <MessageHeartSquareIcon size={40} />
            <h3 className="text-headline3">{t("page.onboarding.code-of-conduct.title")}</h3>
          </div>
          <MultiLineContent>{t("page.onboarding.code-of-conduct.body")}</MultiLineContent>
          <Button
            data-testid="coc-link"
            icon={<Glasses02Icon />}
            styling="ghostPrimary"
            type="link"
            href={sessionUser.codeOfConduct.url}
            target="_blank"
            isExternal
          >
            {t("page.onboarding.code-of-conduct.link.read-code-of-conduct")}
          </Button>
          <div className="mt-4">
            <Button className="w-full" onClick={handleAgree} data-testid="coc-agree-btn">
              {t("page.onboarding.code-of-conduct.btn.agree")}
            </Button>
          </div>
        </div>
      </Paper>
    </main>
  );
}
